import { formatDate, formatDecimalPlace, formatNumber } from "common/formatters";
import Contract, { ContractType, getContractTypeName } from "domain/contract";
import React from "react";
import { getPaymentOptionDisplayName } from "domain/paymentOption";
import { getProducerDisplayName } from "features/settings/producers/producer";
import { getTraderDisplayName } from "features/settings/traders/trader";
import { getDeliveryPeriodDisplayName } from "domain/deliveryPeriod";
import ErrorList from "common/ui/ErrorList";
import { formatCustomerWillSell, getPriceLabelForContract } from "common/formatters/strings";
import { useSelector } from "react-redux";
import IAppStore from "app/IAppStore";
import * as _ from "lodash";
import { getTotalFees } from "./helpers";
import { getProducerAccountDisplayName } from "features/settings/producers/producerAccount";
import contractTypeHelper from "domain/contractTypeHelper";
import unitHelper from "domain/unitHelper";
import useTranslators from "common/translation/useTranslators";
import useUnitConversionRates from "features/settings/commodities/useUnitConversionRates";
import { usePaymentOptions } from "features/merchants/usePaymentOptions";

export type ContractDetailsConfirmationProps = {
    contract: Contract;
    errors: string[];
    showPaymentOption: boolean;
};

const ContractDetailsConfirmation: React.FC<ContractDetailsConfirmationProps> = (props: ContractDetailsConfirmationProps) => {
    const isCustomer = useSelector((m: IAppStore) => m.userState.isCustomer);
    const activeMerchant = useSelector((m: IAppStore) => m.userState.activeMerchant);
    const displayRegionOnAddOffer: boolean = activeMerchant.displayRegionOnAddOffer;
    const displayContractLocation: boolean = activeMerchant.displayContractLocation;
    const unitsConversionRates = useUnitConversionRates();
    const paymentOptionsDetails = usePaymentOptions();
    const showLocalUOM = props.contract?.commodity?.baseUnit.id !== props.contract?.commodity?.localUnit.id;

    const translators = useTranslators();

    const totalFees: number = getTotalFees(props.contract);

    function getPriceText(price: number) {
        return unitHelper.getPriceText(price, showLocalUOM, props.contract.contractCurrency.code, props.contract.commodity, unitsConversionRates);
    }

    return <>
        <table className="table table-striped contract-confirmation">
            <tbody>
                <tr className="highlight">
                    <td>Type</td>
                    <td className="contract-value">{getContractTypeName(props.contract.contractType)}</td>
                </tr>
                <tr>
                    <td>Customer</td>
                    <td className="contract-value">{getProducerDisplayName(props.contract.producer)}</td>
                </tr>
                <tr>
                    <td>Account ID</td>
                    <td className="contract-value">{getProducerAccountDisplayName(props.contract.customerAccount)}</td>
                </tr>
                <tr className="highlight">
                    <td>Customer Will</td>
                    <td className="contract-value">{formatCustomerWillSell(props.contract.customerWillSell)}</td>
                </tr>
                <tr>
                    <td>Commodity</td>
                    <td className="contract-value">{props.contract.commodity.name}</td>
                </tr>
                {displayContractLocation && <tr>
                    <td>Contract Location</td>
                    <td className="contract-value">{props.contract.contractLocation ? props.contract.contractLocation.name : 'Determine Later'}</td>
                </tr>}
                <tr>
                    <td>Location</td>
                    <td className="contract-value">{props.contract.location ? props.contract.location.name : 'Determine Later'}</td>
                </tr>
                {displayRegionOnAddOffer && props.contract.region != null && <tr>
                    <td>Region</td>
                    <td className="contract-value">{props.contract.region.name}</td>
                </tr>}
                {props.contract.deliveryMethod != null && <tr>
                    <td>Delivery Method</td>
                    <td className="contract-value">{translators.deliveryMethods.translate(props.contract.deliveryMethod)}</td>
                </tr>}
                {Boolean(props.contract.freightFee) && <tr>
                    <td>Freight Fee</td>
                    <td className="contract-value">{formatDecimalPlace(props.contract.freightFee)}</td>
                </tr>}
                {props.contract.deliveryPeriod && <tr>
                    <td>Delivery Period</td>
                    <td className="contract-value">{getDeliveryPeriodDisplayName(props.contract.deliveryPeriod)} </td>
                </tr>}
                {props.contract.htaFee != null && <tr>
                    <td>HTA Fee</td>
                    <td className="contract-value">{formatDecimalPlace(props.contract.htaFee)} </td>
                </tr>}
                {props.contract.fees.length > 0 && <tr>
                    <td>Total Fees</td>
                    <td className="contract-value">{getPriceText(totalFees)}</td>
                </tr>}
                {props.contract.futuresContract && <tr>
                    <td>Symbol</td>
                    <td className="contract-value">{props.contract.futuresContract.symbol}</td>
                </tr>}
                {contractTypeHelper.hasBasis(props.contract.contractType) && <tr>
                    <td>Basis</td>
                    <td className="contract-value">{props.contract.basis == null ? '--' :formatDecimalPlace(props.contract.basis)}</td>
                </tr>}
                {Boolean(props.contract.cashPrice) && <><tr className="highlight">
                    <td>{getPriceLabelForContract(props.contract.contractType)}</td>
                    <td className="contract-value">{getPriceText(props.contract.cashPrice)}</td>
                </tr>
                <tr>
                    <td>Final Price</td>
                    <td className="contract-value">{getPriceText(props.contract.finalPrice)}</td>
                </tr></>}
                {Boolean(props.contract.futuresPrice) && contractTypeHelper.isHta(props.contract.contractType) && <tr className="highlight">
                    <td>{getPriceLabelForContract(props.contract.contractType)}</td>
                    <td className="contract-value">{formatDecimalPlace(props.contract.futuresPrice)} {props.contract.contractCurrency.code}</td>
                </tr>}
                <tr className="highlight">
                    <td>{props.contract.contractType === ContractType.Basis ? "Contracted Quantity": "Quantity"}</td>
                    <td className="contract-value">{showLocalUOM ? unitHelper.getQuantityTextWithUnits(props.contract.quantityContracted, props.contract.commodity, unitsConversionRates)
                     : `${formatNumber(props.contract.quantityContracted, '0,0.[00]')} ${props.contract.commodity.localUnit.abbreviation}`}</td>
                </tr>
                <tr>
                    <td>{contractTypeHelper.isCash(props.contract.contractType) ? "Expiration" : "Final Pricing Date"}</td>
                    <td className="contract-value">{formatDate(props.contract.expirationDate)}</td>
                </tr>
                <tr>
                    <td>Trader</td>
                    <td className="contract-value">{getTraderDisplayName(props.contract.trader)}</td>
                </tr>
                {props.showPaymentOption && <tr>
                    <td>Payment Option</td>
                    <td className="contract-value">{getPaymentOptionDisplayName(props.contract.paymentOption, paymentOptionsDetails)}</td>
                </tr>}
                {props.contract.deferredPayDate && <tr>
                    <td>Pay Date</td>
                    <td className="contract-value">{formatDate(props.contract.deferredPayDate)}</td>
                </tr>}
                {props.contract.interestStartDate && <tr>
                    <td>Interest Start Date</td>
                    <td className="contract-value">{formatDate(props.contract.interestStartDate)}</td>
                </tr>}
                {props.contract.interestRate != null && <tr>
                    <td>Interest Rate</td>
                    <td className="contract-value">{props.contract.interestRate}%</td>
                </tr>}
                {props.contract.deliveryTerms && <tr>
                    <td>Delivery Terms</td>
                    <td className="contract-value">{props.contract.deliveryTerms}</td>
                </tr>}
                {props.contract.customerContractId && <tr>
                    <td>Cust. Contract ID</td>
                    <td className="contract-value">{props.contract.customerContractId}</td>
                </tr>}
            </tbody>
        </table>
        {props.contract.comments && <>
            <h3>Comments</h3>
            <p>{props.contract.comments}</p>
        </>}

        {isCustomer && <div className="terms">
            <h5 className="terms-heading">Terms & Conditions</h5>
            <p>By clicking Submit, you are committing to a firm obligation between seller and buyer.</p>
        </div>}

        <ErrorList errors={props.errors}>
                <h3>Errors</h3>
        </ErrorList>
    </>
};

export default ContractDetailsConfirmation;